import { flatten } from "lodash";
import type { InfiniteData } from "react-query";
import { useInfiniteQuery } from "react-query";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoomMessageSearchResult } from "@/react/types/Chat/ChatRoomMessage";

interface Response {
  has_next_page: boolean;
  page: number;
  records: ChatRoomMessageSearchResult[];
}

interface QueryData extends InfiniteData<ChatRoomMessageSearchResult> {
  pagesList: Response[];
}

interface MessageSearchProps {
  chatRoomUuid?: string;
  debouncedValue: string;
}

export const useMessageSearch = ({
  debouncedValue,
  chatRoomUuid,
}: MessageSearchProps) => {
  const fetchPage = (pageParam = 1) =>
    reactQueryGet<Response>(
      internalApi.search.chatRoomMessages.index({
        params: {
          page: pageParam,
          query: debouncedValue,
        },
        chat_room_uuid: chatRoomUuid ?? "",
      }),
    );

  const { data, fetchNextPage, hasNextPage, isInitialLoading } =
    useInfiniteQuery({
      queryKey: ["chat-room-message-search", debouncedValue, chatRoomUuid],
      queryFn: ({ pageParam = 1 }) => fetchPage(pageParam),
      initialPageParam: 1,
      enabled: !!debouncedValue,
      getNextPageParam: (lastPage: any) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      select: ({ pages = [] }): QueryData => ({
        pages: flatten(pages.map(page => page.records)),
        pageParams: pages.map(page => page.page),
        pagesList: pages,
      }),
    });

  const messages = data?.pages ?? [];
  return {
    messages,
    fetchNextPage,
    hasNextPage,
    isLoading: isInitialLoading,
  };
};
